import { useState } from "react";
import { Share2, Copy, Send, MessageCircle } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";

export function eventShareUrl(eventId: string, refUserId?: string | null): string {
  const base = `https://turkey-drive.com/e/${eventId}`;
  return refUserId ? `${base}?ref=${refUserId}` : base;
}

export function eventShareText(title: string): string {
  return `Приходи на мероприятие «${title}» в TurkeyDrive`;
}

export function ShareEventButton({
  eventId,
  title,
  variant = "button",
}: {
  eventId: string;
  title: string;
  variant?: "button" | "icon" | "square";
}) {
  const [open, setOpen] = useState(false);
  const { user } = useAuth();
  const url = eventShareUrl(eventId, user?.id ?? null);
  const text = eventShareText(title);

  async function handleClick(e: React.MouseEvent) {
    e.stopPropagation();
    if (typeof navigator !== "undefined" && typeof navigator.share === "function") {
      try {
        await navigator.share({ title, text, url });
      } catch {
        /* пользователь отменил — ничего не делаем */
      }
      return;
    }
    setOpen((v) => !v);
  }

  async function copyLink() {
    try {
      await navigator.clipboard.writeText(url);
      toast.success("Ссылка скопирована");
    } catch {
      toast.error("Не удалось скопировать ссылку");
    }
    setOpen(false);
  }

  const trigger =
    variant === "square" ? (
      <button
        type="button"
        onClick={handleClick}
        aria-label="Поделиться"
        title="Поделиться"
        className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-xl border border-td-line text-td-teal transition hover:border-td-teal/60 hover:bg-td-teal/10"
      >
        <Share2 className="h-5 w-5" />
      </button>
    ) : variant === "icon" ? (
      <button
        type="button"
        onClick={handleClick}
        aria-label="Поделиться"
        title="Поделиться"
        className="shrink-0 rounded-full border border-td-line p-1.5 text-td-muted transition hover:border-td-teal/60 hover:text-td-teal"
      >
        <Share2 className="h-4 w-4" />
      </button>
    ) : (
      <button
        type="button"
        onClick={handleClick}
        className="inline-flex shrink-0 items-center gap-1.5 rounded-full border border-td-teal px-4 py-2 text-sm font-semibold text-td-teal transition hover:bg-td-teal/10"
      >
        <Share2 className="h-4 w-4" />
        Поделиться
      </button>
    );

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>{trigger}</PopoverTrigger>
      <PopoverContent align="end" className="w-52 p-1">
        <a
          href={`https://wa.me/?text=${encodeURIComponent(`${text} ${url}`)}`}
          target="_blank"
          rel="noopener noreferrer"
          onClick={() => setOpen(false)}
          className="flex items-center gap-2 rounded-md px-2 py-2 text-sm text-td-dark hover:bg-td-soft"
        >
          <MessageCircle className="h-4 w-4" /> WhatsApp
        </a>
        <a
          href={`https://t.me/share/url?url=${encodeURIComponent(url)}&text=${encodeURIComponent(text)}`}
          target="_blank"
          rel="noopener noreferrer"
          onClick={() => setOpen(false)}
          className="flex items-center gap-2 rounded-md px-2 py-2 text-sm text-td-dark hover:bg-td-soft"
        >
          <Send className="h-4 w-4" /> Telegram
        </a>
        <button
          type="button"
          onClick={copyLink}
          className="flex w-full items-center gap-2 rounded-md px-2 py-2 text-left text-sm text-td-dark hover:bg-td-soft"
        >
          <Copy className="h-4 w-4" /> Скопировать ссылку
        </button>
      </PopoverContent>
    </Popover>
  );
}
